import React, {
  UIEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Box } from '@tanium/react-box';
import { LayoutStyleProps } from '@tanium/style-system';
import { useListOverflow } from '../../hooks/useListOverflow';
import { Id } from '../../types/Id';
import { ItemRenderer } from '../Item/ItemRenderer';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { ListProps } from './ListProps';
import { RibbonListHandles } from './RibbonListHandles';

/**
 * Static-specific props used by RibbonListStatic.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface RibbonListStaticProps<TId extends Id, TItem> {
  height?: LayoutStyleProps['height'];
  maxHeight?: LayoutStyleProps['maxHeight'];
  initialScrollOffset?: number;
  onScroll?: (offset: number) => void;
}

interface Props<TId extends Id, TItem>
  extends ListProps<TId, TItem>,
    RibbonListStaticProps<TId, TItem> {}

const emptyStateJsx = (
  <Box height={8} position="relative">
    <EmptyState />
  </Box>
);

/**
 * A component that renders the main body of a StackedRibbonList statically to the DOM. This
 * component should be rendered inside a RibbonListWrapper.
 */
const RibbonListStaticInner = <TId extends Id, TItem>(
  {
    onScroll,
    height,
    itemCount,
    itemIdGetter,
    maxHeight,
    onListOverflowChange,
    initialScrollOffset,
    isLoading,
    isError,
  }: Props<TId, TItem>,
  ref: React.Ref<RibbonListHandles>,
) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useListOverflow(outerRef, onListOverflowChange);

  const scrollToOffset = useCallback((offset: number) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    outerRef.current!.scrollTop = offset;
  }, []);

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      scrollToOffset(0);
    },
    scrollToOffset,
  }));

  const children = useMemo(() => {
    if (itemCount === 0) {
      return emptyStateJsx;
    }

    const items = [];
    for (let i = 0; i < itemCount; i += 1) {
      items.push(<ItemRenderer key={itemIdGetter(i)} index={i} />);
    }

    return items;
  }, [itemCount, itemIdGetter]);

  const handleScroll = useCallback<UIEventHandler<HTMLDivElement>>(
    (e) => {
      onScroll?.(e.currentTarget.scrollTop);
    },
    [onScroll],
  );

  const hasScrolledInitiallyRef = useRef(false);
  const isReadyToDoInitialScroll = !isLoading;

  useEffect(() => {
    if (isReadyToDoInitialScroll && !hasScrolledInitiallyRef.current) {
      hasScrolledInitiallyRef.current = true;

      if (initialScrollOffset) {
        scrollToOffset(initialScrollOffset);
      }
    }
  }, [initialScrollOffset, isReadyToDoInitialScroll, scrollToOffset]);

  return isError && !isLoading ? (
    <ErrorState />
  ) : (
    <Box
      role="rowgroup"
      maxHeight={maxHeight}
      height={height}
      overflow="auto"
      ref={outerRef}
      onScroll={handleScroll}
    >
      <div role="presentation" ref={innerRef}>
        {children}
      </div>
    </Box>
  );
};

export const RibbonListStatic = React.forwardRef(RibbonListStaticInner);
